import { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import {
  AlertCircle,
  ArrowLeft,
  Camera01,
  Check,
  Eye,
  RefreshCw01,
  SunSetting03,
  Target01,
} from "@untitledui/icons"
import { agentsApi, type ImageEvaluationResult } from "@/lib/api/agents"
import type { VehicleSide } from "@/lib/api/types"
import { cx } from "@/lib/utils/cx"
import { PHOTO_COPY, VERDICT_COPY, wrongSideCopy } from "./copy"
import { deriveVerdict } from "./derive-verdict"
import { SHOT_SEQUENCE, silhouetteFor } from "./types"
import type { BodySilhouette, CapturedShot, ShotKey, VinDecodedVehicle } from "./types"
import type { ConsumerIntakeEstimateActions } from "./use-consumer-intake-estimate-state"
import { ReferenceImage } from "./reference-image"

// Quick Estimate 2 — Screen 2.
// Sequenced photo capture: Front → Driver → Passenger → Rear.
// Each shot has the same shape: title, reference image, 3 rules,
// big Take Photo button, small Skip. After capture: thumbnail +
// AI verdict. Good → 0.5s confirmation + 3s "wrong side?"
// affordance + advance to next slot. Bad → hold the user with the
// retake instruction. After 2 retakes on the same slot: "Good
// enough" softening so the user isn't trapped.

interface StepPhotosProps {
  shots: Partial<Record<ShotKey, CapturedShot>>
  closeUps: CapturedShot[]
  vehicle: VinDecodedVehicle | null
  actions: ConsumerIntakeEstimateActions
  onBack: () => void
  onContinue: () => void
  // Injectable so the public consumer flow can swap in the no-auth
  // `/public/agents/images/evaluate` endpoint. Defaults to the authed
  // agent used by the in-store advisor wizard.
  evaluate?: (
    file: File,
    expectedSide?: VehicleSide,
  ) => Promise<ImageEvaluationResult>
}

type Phase =
  // Capturing one of the 4 required slots.
  | { kind: "shot"; slotIndex: number }
  // Asking if the user wants to add damage close-ups.
  | { kind: "close-up-prompt" }
  // Capturing a close-up. The captured shot appends to the closeUps array.
  | { kind: "close-up" }

type SubState =
  | { kind: "ready" }
  // Photo is being uploaded + evaluated by the agent.
  | { kind: "uploading"; previewUrl: string }
  // Photo came back. Verdict drives what we render.
  | { kind: "verdict"; shot: CapturedShot }

export function StepPhotos({
  shots,
  closeUps,
  vehicle,
  actions,
  onBack,
  onContinue,
  evaluate = agentsApi.evaluateImage,
}: StepPhotosProps) {
  const bodyType: BodySilhouette = silhouetteFor(vehicle?.type)
  // Find the first slot that hasn't been captured yet — that's where
  // we resume. If everything's captured already, jump straight to the
  // close-up prompt.
  const firstEmptyIndex = SHOT_SEQUENCE.findIndex((s) => !shots[s.key])
  const initialPhase: Phase =
    firstEmptyIndex === -1
      ? { kind: "close-up-prompt" }
      : { kind: "shot", slotIndex: firstEmptyIndex }

  const [phase, setPhase] = useState<Phase>(initialPhase)
  const [sub, setSub] = useState<SubState>({ kind: "ready" })
  const fileInputRef = useRef<HTMLInputElement>(null)

  const currentSlot =
    phase.kind === "shot" ? SHOT_SEQUENCE[phase.slotIndex] : null

  const handleTakePhotoClick = () => {
    // Light haptic confirms the press before the OS camera takes
    // over (Android only; iOS Safari ignores).
    try {
      navigator.vibrate?.(10)
    } catch {
      // ignore
    }
    fileInputRef.current?.click()
  }

  const handleFile = async (file: File) => {
    const previewUrl = URL.createObjectURL(file)
    setSub({ kind: "uploading", previewUrl })
    try {
      const expectedShot =
        phase.kind === "shot" ? SHOT_SEQUENCE[phase.slotIndex].key : "front"
      const expectedSide =
        phase.kind === "shot" ? SHOT_SEQUENCE[phase.slotIndex].side : undefined
      const result = await evaluate(file, expectedSide)
      const ev = result.evaluation
      const verdict =
        phase.kind === "close-up"
          ? "good" // close-ups skip side check; only quality applies
          : deriveVerdict({
              evaluation: ev,
              expectedShot,
            })

      const shot: CapturedShot = {
        previewUrl,
        uploadedUrl: ev.imageUrl ?? result.upload?.url ?? "",
        detectedSide: (ev.side ?? "FRONT_CENTER") as VehicleSide,
        qualityScore: ev.qualityScore ?? 0,
        qualified: !!ev.qualified,
        qualityIssues: ev.qualityIssues ?? [],
        verdict,
        retakeCount:
          phase.kind === "shot"
            ? (shots[SHOT_SEQUENCE[phase.slotIndex].key]?.retakeCount ?? 0)
            : 0,
        takenAt: Date.now(),
      }

      setSub({ kind: "verdict", shot })
    } catch {
      // Network / upload failure — treat as a soft verdict so the
      // user can retake. We never blame them for the failure.
      const fallback: CapturedShot = {
        previewUrl,
        uploadedUrl: "",
        detectedSide: "FRONT_CENTER",
        qualityScore: 0,
        qualified: false,
        qualityIssues: [],
        verdict: "low-quality",
        retakeCount:
          phase.kind === "shot"
            ? (shots[SHOT_SEQUENCE[phase.slotIndex].key]?.retakeCount ?? 0)
            : 0,
        takenAt: Date.now(),
      }
      setSub({ kind: "verdict", shot: fallback })
    }
  }

  // Auto-advance after a "good" verdict for required slots only.
  // Close-ups stay with a manual confirm (the user often wants to
  // verify framing before moving on).
  const handleAcceptShot = () => {
    if (sub.kind !== "verdict") return
    if (phase.kind === "shot") {
      actions.setShot(currentSlot!.key, sub.shot)
      const nextIndex = phase.slotIndex + 1
      if (nextIndex < SHOT_SEQUENCE.length) {
        setPhase({ kind: "shot", slotIndex: nextIndex })
      } else {
        setPhase({ kind: "close-up-prompt" })
      }
      setSub({ kind: "ready" })
    } else if (phase.kind === "close-up") {
      actions.addCloseUp(sub.shot)
      setSub({ kind: "ready" })
    }
  }

  const handleRetake = () => {
    if (phase.kind === "shot" && sub.kind === "verdict") {
      // Bump retake count so the UI knows when to soften after 2 fails.
      const slotKey = SHOT_SEQUENCE[phase.slotIndex].key
      const existingRetakes = shots[slotKey]?.retakeCount ?? 0
      actions.setShot(slotKey, {
        ...sub.shot,
        retakeCount: existingRetakes + 1,
      })
    }
    setSub({ kind: "ready" })
    setTimeout(() => fileInputRef.current?.click(), 50)
  }

  const handleSkipShot = () => {
    if (phase.kind !== "shot") return
    const nextIndex = phase.slotIndex + 1
    if (nextIndex < SHOT_SEQUENCE.length) {
      setPhase({ kind: "shot", slotIndex: nextIndex })
    } else {
      setPhase({ kind: "close-up-prompt" })
    }
    setSub({ kind: "ready" })
  }

  // Wrong-side correction inline question — shown either as part of a
  // verdict or after a "good" auto-advance for low-confidence shots.
  const handleSideCorrected = (newSide: VehicleSide) => {
    if (sub.kind !== "verdict") return
    const corrected: CapturedShot = {
      ...sub.shot,
      detectedSide: newSide,
      verdict: "good",
    }
    setSub({ kind: "verdict", shot: corrected })
  }

  const handleStartCloseUps = () => {
    setPhase({ kind: "close-up" })
    setSub({ kind: "ready" })
  }

  const handleFinishCloseUps = () => {
    onContinue()
  }

  const handleSkipCloseUps = () => {
    onContinue()
  }

  const handleRemoveCloseUp = (index: number) => {
    actions.removeCloseUp(index)
  }

  const eyebrow =
    phase.kind === "shot"
      ? `Step 2 of 3 · Photo ${phase.slotIndex + 1} of 4`
      : phase.kind === "close-up"
        ? `Step 2 of 3 · Close-up`
        : "Step 2 of 3"
  const title =
    phase.kind === "shot"
      ? (currentSlot?.label ?? "")
      : phase.kind === "close-up-prompt"
        ? PHOTO_COPY.closeUpsTitle
        : PHOTO_COPY.closeUpFromCount(closeUps.length)

  return (
    <div className="space-y-6">
      <header>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-3">
          {eyebrow}
        </p>
        <h2 className="text-2xl md:text-[28px] font-bold text-zinc-900 tracking-tight leading-tight">
          {title}
        </h2>
      </header>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) handleFile(file)
          e.target.value = ""
        }}
        className="hidden"
      />

      <AnimatePresence mode="wait">
        {/* SHOT FLOW — the four required slots */}
        {phase.kind === "shot" && currentSlot && sub.kind === "ready" && (
          <ReadyState
            key={`ready-${phase.slotIndex}`}
            slotKey={currentSlot.key}
            slotLabel={currentSlot.label}
            bodyType={bodyType}
            onTakePhoto={handleTakePhotoClick}
            onSkip={handleSkipShot}
          />
        )}

        {phase.kind === "shot" && sub.kind === "uploading" && (
          <UploadingState key="uploading-shot" previewUrl={sub.previewUrl} />
        )}

        {phase.kind === "shot" && sub.kind === "verdict" && currentSlot && (
          <VerdictState
            key="verdict-shot"
            shot={sub.shot}
            slotKey={currentSlot.key}
            onAccept={handleAcceptShot}
            onRetake={handleRetake}
            onSideCorrected={handleSideCorrected}
          />
        )}

        {/* CLOSE-UP PROMPT */}
        {phase.kind === "close-up-prompt" && (
          <motion.div
            key="close-up-prompt"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            className="space-y-4"
          >
            <p className="text-[14px] text-zinc-500 leading-relaxed">
              Got the four sides. Want to capture any specific damage areas
              up close — dents, scratches, broken parts?
            </p>
            <button
              type="button"
              onClick={handleStartCloseUps}
              className="w-full h-14 px-6 rounded-2xl bg-zinc-900 hover:bg-black text-white shadow-lg shadow-zinc-900/15 hover:shadow-zinc-900/25 transition-all duration-150 flex items-center justify-center gap-2 text-[15px] font-semibold"
            >
              <Camera01 className="size-5" aria-hidden />
              {PHOTO_COPY.closeUpsYes}
            </button>
            <button
              type="button"
              onClick={handleSkipCloseUps}
              className="w-full h-12 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 text-[14px] font-semibold text-zinc-700 transition-colors duration-150"
            >
              {PHOTO_COPY.closeUpsNo}
            </button>
          </motion.div>
        )}

        {/* CLOSE-UP LOOP */}
        {phase.kind === "close-up" && sub.kind === "ready" && (
          <CloseUpReadyState
            key={`close-up-ready-${closeUps.length}`}
            existingCount={closeUps.length}
            onTakePhoto={handleTakePhotoClick}
            onDone={handleFinishCloseUps}
            onRemove={handleRemoveCloseUp}
            shots={closeUps}
          />
        )}

        {phase.kind === "close-up" && sub.kind === "uploading" && (
          <UploadingState key="uploading-closeup" previewUrl={sub.previewUrl} />
        )}

        {phase.kind === "close-up" && sub.kind === "verdict" && (
          <CloseUpVerdictState
            key="close-up-verdict"
            shot={sub.shot}
            onAccept={handleAcceptShot}
            onRetake={handleRetake}
          />
        )}
      </AnimatePresence>

      {/* Always-available back. Doesn't trap. */}
      <div className="pt-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 h-9 -ml-1 pl-1.5 pr-3 rounded-lg text-[12.5px] font-medium text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100/70 transition-all duration-150"
        >
          <ArrowLeft className="size-3.5" aria-hidden />
          Back to vehicle
        </button>
      </div>
    </div>
  )
}

/* ── Sub-states ─────────────────────────────────── */

function ReadyState({
  slotKey,
  slotLabel,
  bodyType,
  onTakePhoto,
  onSkip,
}: {
  slotKey: ShotKey
  slotLabel: string
  bodyType: BodySilhouette
  onTakePhoto: () => void
  onSkip: () => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
      className="space-y-5"
    >
      <ReferenceImage shot={slotKey} bodyType={bodyType} />
      <RuleStrip />
      <button
        type="button"
        onClick={onTakePhoto}
        className="w-full h-16 px-6 rounded-2xl bg-zinc-900 hover:bg-black text-white shadow-xl shadow-zinc-900/20 hover:shadow-zinc-900/30 transition-all duration-150 flex items-center justify-center gap-3"
        aria-label={`${PHOTO_COPY.takePhoto} — ${slotLabel}`}
      >
        <Camera01 className="size-6" aria-hidden />
        <span className="text-[17px] font-semibold">{PHOTO_COPY.takePhoto}</span>
      </button>
      {/* Skip — peer-weight outlined secondary, equal height
          presence to Take Photo's surrounding rhythm. Always visible
          alongside the primary action so the user has a clear out. */}
      <button
        type="button"
        onClick={onSkip}
        className="w-full h-14 px-6 rounded-2xl border-2 border-zinc-200 bg-white hover:bg-zinc-50 hover:border-zinc-300 text-zinc-700 transition-all duration-150 flex items-center justify-center gap-2 text-[14.5px] font-semibold"
      >
        {PHOTO_COPY.skipShot}
      </button>
    </motion.div>
  )
}

function RuleStrip() {
  return (
    <div className="flex items-center justify-around rounded-xl bg-zinc-50 border border-zinc-200/70 px-3 py-2.5">
      <Rule icon={<Target01 className="size-3.5" />} label={PHOTO_COPY.rules.standBack} />
      <Rule icon={<Eye className="size-3.5" />} label={PHOTO_COPY.rules.wholeVehicle} />
      <Rule icon={<SunSetting03 className="size-3.5" />} label={PHOTO_COPY.rules.goodLight} />
    </div>
  )
}

function Rule({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex items-center gap-1.5 text-zinc-600">
      <span className="text-zinc-400">{icon}</span>
      <span className="text-[11.5px] font-semibold">{label}</span>
    </div>
  )
}

function UploadingState({ previewUrl }: { previewUrl: string }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="space-y-4"
    >
      <div className="rounded-xl overflow-hidden border border-zinc-200 bg-zinc-50">
        <img
          src={previewUrl}
          alt="Just captured"
          className="w-full max-h-72 object-cover"
        />
      </div>
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-zinc-50 border border-zinc-200">
        <span className="size-4 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" />
        <p className="text-[14px] font-medium text-zinc-700">{PHOTO_COPY.checking}</p>
      </div>
    </motion.div>
  )
}

function VerdictState({
  shot,
  slotKey,
  onAccept,
  onRetake,
  onSideCorrected,
}: {
  shot: CapturedShot
  slotKey: ShotKey
  onAccept: () => void
  onRetake: () => void
  onSideCorrected: (s: VehicleSide) => void
}) {
  const [showWrongSide, setShowWrongSide] = useState(true)
  const isGood = shot.verdict === "good"
  const softened = shot.retakeCount >= 2

  // After a 'good' shot, the wrong-side affordance is visible for
  // 3 seconds, then collapses. The user can still re-open it from
  // the trailing chip if they need to.
  useEffect(() => {
    if (!isGood) return
    const t = setTimeout(() => setShowWrongSide(false), 3000)
    return () => clearTimeout(t)
  }, [isGood])

  if (shot.verdict === "wrong-side") {
    // Inline correction prompt — replaces the (deleted) tag-sides
    // step for the low-confidence case. Confirm copy uses the
    // expected side label so the user re-affirms the named side.
    const expectedLabel =
      SHOT_SEQUENCE.find((s) => s.key === slotKey)?.label ?? "side"
    const confirmLabel = `Yes, this is the ${expectedLabel.toLowerCase()}`
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.18 }}
        className="space-y-4"
      >
        <div className="rounded-xl overflow-hidden border border-zinc-200 bg-zinc-50">
          <img
            src={shot.previewUrl}
            alt="Just captured"
            className="w-full max-h-72 object-cover"
          />
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
          <p className="text-[14px] font-semibold text-amber-900 leading-snug mb-3">
            {wrongSideCopy(slotKey)}
          </p>
          <div className="grid grid-cols-2 gap-2">
            <SideButton
              label={confirmLabel}
              onClick={() => {
                onSideCorrected(SHOT_SEQUENCE.find((s) => s.key === slotKey)!.side)
              }}
              tone="primary"
            />
            <SideButton
              label="No, retake it"
              onClick={onRetake}
              tone="secondary"
            />
          </div>
        </div>
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.18 }}
      className="space-y-4"
    >
      <div
        className={cx(
          "rounded-xl overflow-hidden border-2 bg-zinc-50",
          isGood ? "border-emerald-300" : "border-amber-300",
        )}
      >
        <img
          src={shot.previewUrl}
          alt="Just captured"
          className="w-full max-h-72 object-cover"
        />
      </div>

      {/* Verdict banner — system blames itself when bad, celebrates briefly when good */}
      <div
        className={cx(
          "rounded-xl border px-4 py-3 flex items-center gap-3",
          isGood
            ? "border-emerald-200 bg-emerald-50/50"
            : "border-amber-200 bg-amber-50/50",
        )}
      >
        {isGood ? (
          <div className="size-8 rounded-full bg-emerald-500 grid place-items-center shadow-sm shadow-emerald-600/30 shrink-0">
            <Check className="size-4 text-white" strokeWidth={3} aria-hidden />
          </div>
        ) : (
          <AlertCircle className="size-5 text-amber-600 shrink-0" aria-hidden />
        )}
        <p
          className={cx(
            "text-[14px] font-semibold leading-snug",
            isGood ? "text-emerald-900" : "text-amber-900",
          )}
        >
          {VERDICT_COPY[shot.verdict]}
        </p>
      </div>

      {/* Soften after 2 fails — let the user accept the photo and move on */}
      {!isGood && softened && (
        <p className="text-[13px] text-zinc-500 leading-relaxed">
          {PHOTO_COPY.goodEnough}
        </p>
      )}

      {isGood ? (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onAccept}
            className="w-full h-14 px-6 rounded-2xl bg-zinc-900 hover:bg-black text-white shadow-lg shadow-zinc-900/15 hover:shadow-zinc-900/25 transition-all duration-150 flex items-center justify-center gap-2 text-[15px] font-semibold"
          >
            {PHOTO_COPY.next}
          </button>
          {showWrongSide && (
            <button
              type="button"
              onClick={onRetake}
              className="w-full h-9 text-[12px] font-medium text-zinc-500 hover:text-zinc-900 transition-colors duration-150"
            >
              {PHOTO_COPY.wrongSidePrompt}
            </button>
          )}
        </div>
      ) : softened ? (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={onAccept}
            className="w-full h-14 px-6 rounded-2xl bg-zinc-900 hover:bg-black text-white shadow-lg shadow-zinc-900/15 hover:shadow-zinc-900/25 transition-all duration-150 flex items-center justify-center gap-2 text-[15px] font-semibold"
          >
            {PHOTO_COPY.useThisPhoto}
          </button>
          <button
            type="button"
            onClick={onRetake}
            className="w-full h-11 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 text-[13.5px] font-semibold text-zinc-700 transition-colors duration-150 flex items-center justify-center gap-1.5"
          >
            <RefreshCw01 className="size-3.5" aria-hidden />
            {PHOTO_COPY.tryAgain}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={onRetake}
          className="w-full h-14 px-6 rounded-2xl bg-zinc-900 hover:bg-black text-white shadow-lg shadow-zinc-900/15 hover:shadow-zinc-900/25 transition-all duration-150 flex items-center justify-center gap-2 text-[15px] font-semibold"
        >
          <RefreshCw01 className="size-4" aria-hidden />
          {PHOTO_COPY.retake}
        </button>
      )}
    </motion.div>
  )
}

function SideButton({
  label,
  onClick,
  tone,
}: {
  label: string
  onClick: () => void
  tone: "primary" | "secondary"
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "h-12 px-4 rounded-xl text-[13.5px] font-semibold transition-all duration-150",
        tone === "primary"
          ? "bg-zinc-900 hover:bg-black text-white shadow-sm"
          : "border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-700",
      )}
    >
      {label}
    </button>
  )
}

function CloseUpReadyState({
  existingCount,
  onTakePhoto,
  onDone,
  onRemove,
  shots,
}: {
  existingCount: number
  onTakePhoto: () => void
  onDone: () => void
  onRemove: (i: number) => void
  shots: CapturedShot[]
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.18 }}
      className="space-y-5"
    >
      {existingCount > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {shots.map((shot, i) => (
            <div
              key={i}
              className="relative aspect-square rounded-xl overflow-hidden border border-zinc-200 group"
            >
              <img
                src={shot.previewUrl}
                alt={`Close-up ${i + 1}`}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="absolute top-1 right-1 size-6 rounded-full bg-zinc-900/70 text-white text-[10px] font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-150 grid place-items-center"
                aria-label={`Remove close-up ${i + 1}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <RuleStrip />
      <button
        type="button"
        onClick={onTakePhoto}
        className="w-full h-16 px-6 rounded-2xl bg-zinc-900 hover:bg-black text-white shadow-xl shadow-zinc-900/20 hover:shadow-zinc-900/30 transition-all duration-150 flex items-center justify-center gap-3"
      >
        <Camera01 className="size-6" aria-hidden />
        <span className="text-[17px] font-semibold">
          {existingCount === 0 ? PHOTO_COPY.takePhoto : PHOTO_COPY.closeUpAdd}
        </span>
      </button>
      <button
        type="button"
        onClick={onDone}
        className="w-full h-12 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 text-[14px] font-semibold text-zinc-700 transition-colors duration-150"
      >
        {PHOTO_COPY.closeUpDone}
      </button>
    </motion.div>
  )
}

function CloseUpVerdictState({
  shot,
  onAccept,
  onRetake,
}: {
  shot: CapturedShot
  onAccept: () => void
  onRetake: () => void
}) {
  const isGood = shot.verdict === "good"
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.18 }}
      className="space-y-4"
    >
      <div
        className={cx(
          "rounded-xl overflow-hidden border-2 bg-zinc-50",
          isGood ? "border-emerald-300" : "border-amber-300",
        )}
      >
        <img
          src={shot.previewUrl}
          alt="Close-up just captured"
          className="w-full max-h-72 object-cover"
        />
      </div>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onAccept}
          className="w-full h-14 px-6 rounded-2xl bg-zinc-900 hover:bg-black text-white shadow-lg shadow-zinc-900/15 hover:shadow-zinc-900/25 transition-all duration-150 flex items-center justify-center gap-2 text-[15px] font-semibold"
        >
          Add to estimate
        </button>
        <button
          type="button"
          onClick={onRetake}
          className="w-full h-11 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 text-[13.5px] font-semibold text-zinc-700 transition-colors duration-150 flex items-center justify-center gap-1.5"
        >
          <RefreshCw01 className="size-3.5" aria-hidden />
          {PHOTO_COPY.retake}
        </button>
      </div>
    </motion.div>
  )
}
